import { forecastsQuery } from "@common/api/forecast"
import { Button } from "@common/components/ui/button"
import { useMaxWidth } from "@common/hooks/use-mobile"
import { useHasPermission } from "@common/hooks/use-permissions"
import {
  createFileRoute,
  Link,
  Outlet,
  useMatches,
  useNavigate,
} from "@tanstack/react-router"
import { ActiveFilterBar } from "@/components/layout/active-filter-bar"
import { ColumnChips } from "@/components/layout/column-chips"
import { DETAIL_SHEET_BREAKPOINT } from "@/components/layout/detail-panel"
import { DisplayOptions } from "@/components/layout/display-options"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { useColumnSizing } from "@/hooks/use-column-layout"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import { ForecastTable } from "./-components/forecast-table"
import {
  COLUMN_LABELS,
  COLUMN_SIZING,
  COLUMNS,
  type Column,
  DEFAULT_COLUMNS,
  FIXED_COLUMNS,
} from "./-lib/columns"
import {
  type ForecastFilterValues,
  forecastFilterConfigs,
  hasForecastFilters,
  NO_FORECAST_FILTERS,
} from "./-lib/filters"
import { requireForecastPages } from "./-lib/gate"
import {
  displayOf,
  FORECASTS,
  type ForecastsDisplay,
  type ForecastsSearch,
  forecastsSearchSchema,
  GROUPING_LABELS,
  GROUPINGS,
  TABS,
  tabLabel,
} from "./-lib/search"

export const Route = createFileRoute("/_layout/forecasts")({
  staticData: { breadcrumb: "Forecast" },
  validateSearch: (search) => forecastsSearchSchema.parse(search),
  beforeLoad: ({ context: { queryClient } }) =>
    requireForecastPages(queryClient),
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(forecastsQuery(FORECASTS)),
  component: ForecastsPage,
})

function useUpdateSearch(): (patch: Partial<ForecastsSearch>) => void {
  const navigate = useNavigate()
  return (patch) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}

function ForecastsPage(): React.ReactElement {
  const search = Route.useSearch()
  const display = displayOf(search)
  const update = useUpdateSearch()
  const [ref, width] = useWidth<HTMLDivElement>()
  const sizing = useColumnSizing(display.columns, COLUMN_SIZING)
  const fit = fitColumns(display.columns, width, sizing)
  const open = useMatches({
    select: (matches) =>
      matches.some((match) => match.staticData.detailSidebar),
  })
  const compact = useMaxWidth(DETAIL_SHEET_BREAKPOINT)

  return (
    <ListPage
      toolbar={<Toolbar display={display} hidden={fit.hidden} />}
      pane={open && !compact ? "split" : "closed"}
      detail={<Outlet />}
      scrollRef={ref}
    >
      <ActiveFilters />
      <ForecastTable
        search={search}
        display={display}
        columns={fit.shown}
        onSort={(column) => update(sortSearch(display, column))}
        onClear={() => update({ ...NO_FORECAST_FILTERS, search: undefined })}
      />
    </ListPage>
  )
}

function Toolbar({
  display,
  hidden,
}: {
  display: ForecastsDisplay
  hidden: Column[]
}): React.ReactElement {
  const values = Route.useSearch()
  const { tab = "active", search = "" } = values
  const update = useUpdateSearch()
  const canCreate = useHasPermission("forecast:create")

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={TABS}
          tab={tab}
          label={tabLabel}
          onSelect={(next) =>
            update({ tab: next === "active" ? undefined : next })
          }
        />
      }
      actions={
        <>
          <NameFilter
            search={search}
            onSearch={(next) => update({ search: next || undefined })}
            placeholder="Search forecasts…"
          />
          <FilterButton
            active={hasForecastFilters(values)}
            configs={forecastFilterConfigs}
            onChange={update}
          />
          <DisplayOptions
            groupings={GROUPINGS}
            labels={GROUPING_LABELS}
            group={display.group}
            subgroup={undefined}
            onChange={(next) => update({ group: next.group })}
          >
            <ColumnChips
              options={COLUMNS}
              labels={COLUMN_LABELS}
              fixed={FIXED_COLUMNS}
              columns={display.columns}
              hidden={hidden}
              onChange={(next) => update(columnsSearch(next))}
            />
          </DisplayOptions>
          {canCreate && (
            <Button asChild size="sm">
              <Link to="/forecasts/new" search={true}>
                New forecast
              </Link>
            </Button>
          )}
        </>
      }
    />
  )
}

function columnsSearch(columns: Column[]): Partial<ForecastsSearch> {
  const defaults =
    columns.length === DEFAULT_COLUMNS.length &&
    columns.every((column) => DEFAULT_COLUMNS.includes(column))
  return { columns: defaults ? undefined : columns }
}

function sortSearch(
  display: ForecastsDisplay,
  column: Column,
): Partial<ForecastsSearch> {
  const direction =
    column === display.sort && display.direction === "asc" ? "desc" : "asc"
  const byDefault = column === "number" && direction === "desc"
  return byDefault
    ? { sort: undefined, direction: undefined }
    : { sort: column, direction }
}

function ActiveFilters(): React.ReactElement {
  const { basis, creator } = Route.useSearch()
  const update = useUpdateSearch()
  const values: ForecastFilterValues = { basis, creator }

  return (
    <ActiveFilterBar
      active={hasForecastFilters(values)}
      configs={forecastFilterConfigs}
      values={values}
      onChange={update}
      onClear={() => update(NO_FORECAST_FILTERS)}
    />
  )
}
